import { buildApiUrl } from "../../../config.js";
import React, { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExcelUpload from "./ExcelUpload";
import * as XLSX from "xlsx";
import { PulseLoader } from "react-spinners";
import { HiOutlineTrash } from "react-icons/hi";
import { Plus, X } from "lucide-react";

const AddItemModal = ({ onClose, onAddItems }) => {
  const [items, setItems] = useState([
    {
      itemCode: "",
      categoryName: "",
      itemName: "",
      unit: "",
      pcsPerCtn: "",
      wtPerCtn: "",
      cbmPerCtn: "",
      itemImageLink: "",
      itemImage: null,
    },
  ]);
  const [loading, setLoading] = useState(false);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const updatedItems = [...items];
    updatedItems[index][name] = value;
    setItems(updatedItems);
  };

  const handleImageChange = (index, e) => {
    const updatedItems = [...items];
    updatedItems[index].itemImage = e.target.files[0];
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        itemCode: "",
        categoryName: "",
        itemName: "",
        unit: "",
        pcsPerCtn: "",
        wtPerCtn: "",
        cbmPerCtn: "",
        itemImageLink: "",
        itemImage: null,
      },
    ]);
  };

  const removeItem = (index) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("No authentication token found!");
      return;
    }

    // Validate required fields
    const invalidItems = [];
    items.forEach((item, index) => {
      if (!item.itemCode || !item.itemCode.trim()) {
        invalidItems.push(`Item ${index + 1}: Item Code is required`);
      }
      if (!item.categoryName || !item.categoryName.trim()) {
        invalidItems.push(`Item ${index + 1}: Category is required`);
      }
      if (!item.itemName || !item.itemName.trim()) {
        invalidItems.push(`Item ${index + 1}: Item Name is required`);
      }
    });

    if (invalidItems.length > 0) {
      toast.error(invalidItems.join(". "));
      return;
    }

    setLoading(true);

    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][itemCode]`, item.itemCode);
      formData.append(`items[${index}][categoryName]`, item.categoryName);
      formData.append(`items[${index}][itemName]`, item.itemName);
      // Only append optional fields if they have values
      if (item.unit && item.unit.trim() !== '') {
        formData.append(`items[${index}][unit]`, item.unit);
      }
      if (item.pcsPerCtn && item.pcsPerCtn !== '') {
        formData.append(`items[${index}][pcsPerCtn]`, item.pcsPerCtn);
      }
      if (item.wtPerCtn && item.wtPerCtn !== '') {
        formData.append(`items[${index}][wtPerCtn]`, item.wtPerCtn);
      }
      if (item.cbmPerCtn && item.cbmPerCtn !== '') {
        formData.append(`items[${index}][cbmPerCtn]`, item.cbmPerCtn);
      }
      if (item.itemImageLink && item.itemImageLink.trim() !== '') {
        formData.append(`items[${index}][itemImageLink]`, item.itemImageLink);
      }
      if (item.itemImage) {
        formData.append(`items[${index}][itemImage]`, item.itemImage);
      }
    });

    try {
      const response = await fetch(
        buildApiUrl("/api/categories/create"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.categories)) {
          onAddItems(data.categories);
          onClose();
          toast.success("Items added successfully!");
        } else {
          toast.error("Received invalid data from API!");
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to add items");
      }
    } catch (error) {
      toast.error("An error occurred while adding items");
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleData = () => {
    const sampleData = [
      [
        "itemCode",
        "categoryName",
        "itemName",
        "unit",
        "pcsPerCtn",
        "wtPerCtn",
        "cbmPerCtn",
        "itemImageLink",
      ],
      [
        "001",
        "Electronics",
        "Smartphone",
        "Box",
        10,
        5,
        0.1,
        "http://example.com/image1.jpg",
      ],
      [
        "002",
        "Furniture",
        "Chair",
        "Pieces",
        2,
        7,
        0.3,
        "http://example.com/image2.jpg",
      ],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample Data");
    XLSX.writeFile(wb, "sample_data.xlsx");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div
        className="w-full max-w-7xl rounded-lg bg-white shadow-xl overflow-y-auto"
        style={{ maxHeight: "90vh", scrollbarWidth: "thin" }}
      >
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Items</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-2">
          <div className="mb-2 flex flex-col sm:flex-row sm:items-end gap-4 border w-fit p-2 rounded-md bg-gray-50">
            <ExcelUpload onDataLoaded={(data) => setItems(data)} />
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                downloadSampleData();
              }}
              className="rounded-md font-medium bg-green-600 px-4 py-2 text-xs text-white hover:bg-green-700"
            >
              Format
            </a>
          </div>

          {loading && (
            <div className="flex justify-center items-center gap-3 mb-4 text-gray-600">
              <PulseLoader color="#10b981" size={10} />
              <span>Uploading Items...</span>
            </div>
          )}

          <div className="mb-4">
            <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_1fr_80px_80px_80px_1fr_120px_20px] gap-2 text-xs font-medium tracking-wide py-2 bg-gray-200 text-gray-700 rounded-md mb-2 items-center">
              <span className="pl-2">Item Code <span className="text-red-500">*</span></span>
              <span>Category <span className="text-red-500">*</span></span>
              <span>Item Name <span className="text-red-500">*</span></span>
              <span>Unit</span>
              <span>PCS/Carton</span>
              <span>Weight/Carton</span>
              <span>CBM/Carton</span>
              <span>Image URL</span>
              <span>Upload Image</span>
              <span></span>
            </div>

            {items.map((item, index) => (
              <div
                key={index}
                className="mb-2 rounded-md border sm:border-0 border-gray-200 bg-gray-50 p-3 sm:p-0"
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_80px_80px_80px_1fr_120px_20px] items-center">
                  {[
                    { label: "Item Code", name: "itemCode", type: "text", required: true },
                    { label: "Category", name: "categoryName", type: "text", required: true },
                    { label: "Item Name", name: "itemName", type: "text", required: true },
                    { label: "Unit", name: "unit", type: "text", required: false },
                    {
                      label: "PCS per Carton",
                      name: "pcsPerCtn",
                      type: "number",
                      required: false,
                    },
                    {
                      label: "Weight per Carton",
                      name: "wtPerCtn",
                      type: "number",
                      required: false,
                    },
                    {
                      label: "CBM per Carton",
                      name: "cbmPerCtn",
                      type: "number",
                      required: false,
                    },
                    { label: "Image URL", name: "itemImageLink", type: "url", required: false },
                    { label: "Upload Image", name: "file", type: "file", required: false },
                  ].map((field, i) => (
                    <div key={i} className="flex flex-col">
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.type === "file" ? (
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageChange(index, e)}
                          className="w-full rounded-md border border-gray-300 text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <input
                          type={field.type}
                          name={field.name}
                          value={item[field.name] || ""}
                          onChange={(e) => handleChange(index, e)}
                          required={field.required}
                          className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  ))}
                  {items.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="flex items-center justify-center gap-1 text-xs text-red-600 hover:text-red-800"
                    >
                      <HiOutlineTrash size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              onClick={handleAddItem}
              className="flex items-center justify-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            >
              <Plus size={16} />
              Add Item
            </button>
            <button
              onClick={onClose}
              className="rounded-md bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:bg-indigo-400"
            >
              {loading ? "Processing..." : "Save Items"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;
